import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  useCreateChannelMutation,
  useLazyGetMembersBySearchForChannelQuery,
  useLazyGetChannelTeamListQuery
} from 'app/services/group.service'
import { FixMeLater } from 'types'
import { IChannelMember } from 'types/user.types'
import { MODALS, PLACEHOLDERS, TABS } from 'locales/locales'
import General from './General'
import Members from 'components/members/Members'
import CustomModal from '../../../components/modals/CustomModal'
import { useAppSelector } from 'app/hooks'
import {
  IRolePermissions,
  TeamAndChannelPermissions,
} from 'types/role.types'
import { isFlagSetIn } from 'utils/helpers/isSetInFlags'

interface ICreateChannelModalProps {
  groupId: string
  onClose: () => void
}

interface ITeamOption {
  _id: string
  name: string
}

const getIsEmail = (email: string) => {
  const emailRegex = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/
  return emailRegex.test(email)
}

const CreateChannelModal = ({
  groupId,
  onClose,
}: ICreateChannelModalProps) => {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const [generalInfo, setGeneralInfo] = useState({
    name: '',
    description: '',
    isPrivate: false,
  })
  const [getChannelTeamList] =   useLazyGetChannelTeamListQuery()
    const [members, setMembers] = useState<IChannelMember[]>([])
  const [membersBySearch, setMembersBySearch] = useState<IChannelMember[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [teamSelectValue, setTeamSelectValue] = useState('')
  const [selectedTeams, setSelectedTeams] = useState<ITeamOption[]>([])

  const [teamList, setTeamList] = useState<ITeamOption[]>([])

  const [createChannel] = useCreateChannelMutation()
  const [getMembersBySearch] = useLazyGetMembersBySearchForChannelQuery()

  const permissions = useAppSelector<IRolePermissions>(
    state => state.auth.permissions
  )

  const isAdmin = isFlagSetIn(
    [TeamAndChannelPermissions.CREATE_CHANNEL],
    permissions.teamAndChannel
  )



  useEffect(() => {
    fetchTeams()
  }, [])
  
  const fetchTeams = async () => {
    try {
      const data = await getChannelTeamList({})
      if (data?.data) {
        const list = data.data.map((team: FixMeLater) => ({
          ...team,
          title: team.title || team.name,
          value: team.value || team._id,
          id: team.id || team._id,
        }))
        setTeamList(list)
        return data?.data
      }
    } catch (error) {
      console.error(error)
    }
  }

  const onSelectTeam = (teamId: string) => {
    if (!teamId) return

    const selectedTeam = teamList.find(team => team._id === teamId)
    if (!selectedTeam) return

    const hasTeam = selectedTeams.some(team => team._id === selectedTeam._id)
    if (hasTeam) {
      setTeamSelectValue('')
      return
    }

    setSelectedTeams(prev => [...prev, selectedTeam])
    setTeamSelectValue('')
  }

  const onRemoveTeam = (teamId: string) => {
    setSelectedTeams(prev => prev.filter(team => team._id !== teamId))
  }

  const onCreate = async () => {
    try {
      setIsLoading(true)


      

      const result: FixMeLater = await createChannel({
        ...generalInfo,
        groupId,
        members:members.map(member => member._id),
        teamIds: selectedTeams.map(team => team._id),
      })

      if (result?.data?.success) {
        onClose()
        navigate(`/channels/${result.data.data._id}`)
      }
    } finally {
      setIsLoading(false)
    }
  }

  const onChangeGeneralInfo = (name: string, value: string | boolean) => {
    setGeneralInfo(prev => ({
      ...prev,
      [name]: value,
    }))
  }

  const onSelectMember = (user: IChannelMember) => {
    const hasUser = members.some(
      member => member._id === user._id || member.email === user.email
    )

    if (hasUser) return

    setMembers(prev => [...prev, user])
  }

  const onRemoveUser = (user: IChannelMember) => {
    const removedData = members.filter(
      member => member._id !== user._id && member.email !== user.email
    )

    setMembers(removedData)
  }

  const onAssingAsModerator = async (user: IChannelMember) => {
    console.log(user)
  }

  const onSearch = async (text: string) => {
    try {
      const result: FixMeLater = await getMembersBySearch({ searchName: text })

      const filteredUsers = result.data.data.filter(
        (fetchedUser: IChannelMember) => {
          const findedMember = members.findIndex(
            member =>
              member._id === fetchedUser._id ||
              member.email === fetchedUser.email
          )

          return findedMember === -1
        }
      )

      if (getIsEmail(text)) {
        const hasUser = members.find(member => member.email === text)

        if (!hasUser) {
          filteredUsers.push({ email: text })
        }
      }

      setMembersBySearch(filteredUsers)
    } catch (e) {
      console.log(e)
    }
  }

  return (
    <CustomModal
      title={t(MODALS.CREATE_NEW_CHANNEL_TITLE)}
      onOk={onCreate}
      closeModal={onClose}
      onCancel={onClose}
      loading={isLoading}
      tab={[
        { title: t(TABS.GENERAL), action: true },
        { title: t(TABS.MEMBERS), action: true },
      ]}
      hasMember
    >
      <General {...generalInfo} onChange={onChangeGeneralInfo} />

      <>
        <div style={{ marginBottom: '16px' }}>
          <div
            style={{
              fontWeight: 600,
              fontSize: '14px',
              marginBottom: '8px',
            }}
          >
            Teams
          </div>

          <select
            value={teamSelectValue}
            onChange={e => {
              const value = e.target.value
              setTeamSelectValue(value)
              onSelectTeam(value)
            }}
            style={{
              width: '100%',
              height: '40px',
              borderRadius: '8px',
              border: '1px solid #d1d5db',
              padding: '0 12px',
              marginBottom: '12px',
            }}
          >
            <option value="">Team seç</option>
            {teamList.map(team => (
              <option key={team._id} value={team._id}>
                {team.name}
              </option>
            ))}
          </select>

          {!!selectedTeams.length && (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                marginBottom: '12px',
              }}
            >
              {selectedTeams.map(team => (
                <div
                  key={team._id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '6px 10px',
                    borderRadius: '999px',
                    background: '#f3f4f6',
                  }}
                >
                  <span>{team.name}</span>

                  <button
                    type="button"
                    onClick={() => onRemoveTeam(team._id)}
                    style={{
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      fontSize: '14px',
                      lineHeight: 1,
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <Members
          isSearch
          isAddMember
          isRemove
          data={members}
          searchedData={membersBySearch}
          isAdmin={isAdmin}
          placeholder={t(PLACEHOLDERS.MEMBERS_PLACEHOLDER)}
          isChannelCreation={true}
          onChangeSearchText={onSearch}
          onSelectMember={onSelectMember}
          onRemoveUser={onRemoveUser}
          onAssingAsModerator={onAssingAsModerator}
        />
      </>
    </CustomModal>
  )
}

export default CreateChannelModal